/**
 * CRC-16/IBM — also catalogued as CRC-16/ARC.
 *
 * Reflected polynomial 0xA001 (the bit-reversal of 0x8005), init 0x0000, no
 * final XOR, input and output reflected.
 *
 * IT IS NOT CRC-16/CCITT. CCITT (poly 0x1021, init 0xFFFF) rejects every valid
 * Teltonika frame: the canonical Codec 8 example checksums to 0xC7CF under IBM
 * and to an unrelated value under CCITT. CCITT is the constant everyone reaches
 * for first because it is the one printed in most protocol books, and reaching
 * for it here produces a decoder that silently drops 100% of live traffic while
 * blaming the device. crc16.test.ts asserts the difference on a real frame so
 * the mistake cannot be reintroduced quietly.
 *
 * Teltonika transmits the 16-bit result in the low half of a 4-byte big-endian
 * field; the high half is always zero. Comparing against the full 32-bit field
 * (rather than masking it down to 16 bits) is deliberate at the call sites — a
 * non-zero high half means the frame is not what it claims to be.
 */
/**
 * Byte-at-a-time lookup table, built once at module load.
 *
 * The bit-at-a-time form is eight times slower, and this runs on every frame on
 * the ingest hot path and again on every frame the simulator emits.
 */
const CRC16_IBM_TABLE = (() => {
    const table = new Uint16Array(256);
    for (let index = 0; index < 256; index += 1) {
        let value = index;
        for (let bit = 0; bit < 8; bit += 1) {
            value = (value & 1) === 1 ? (value >>> 1) ^ 0xa001 : value >>> 1;
        }
        table[index] = value;
    }
    return table;
})();
/**
 * CRC-16/IBM over `bytes[start, end)`.
 *
 * The range is clamped rather than validated: callers pass offsets derived from
 * a length field that a hostile device controls, and a checksum helper is the
 * wrong place to throw.
 */
export function crc16Ibm(bytes, start = 0, end = bytes.length) {
    const from = start < 0 ? 0 : start;
    const to = end > bytes.length ? bytes.length : end;
    let crc = 0;
    for (let index = from; index < to; index += 1) {
        // The index is masked to 0..255 and the table has exactly 256 entries, so
        // the assertion is discharged by construction. It exists only because
        // noUncheckedIndexedAccess widens every typed-array read to `| undefined`.
        const entry = CRC16_IBM_TABLE[(crc ^ (bytes[index] ?? 0)) & 0xff];
        crc = (crc >>> 8) ^ entry;
    }
    return crc & 0xffff;
}
/** The check value every CRC-16/IBM implementation must reproduce for "123456789". */
export const CRC16_IBM_CHECK_VALUE = 0xbb3d;
//# sourceMappingURL=crc16.js.map